import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import logo from '../../assets/logo.png';
import { GoogleLogin } from '@react-oauth/google';
import './Login.css';

const Login = () => {
  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '' });
  const [otp, setOtp] = useState(['', '', '', '']);
  const inputRefs = useRef([]);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const isNavigatingToRoleSelection = useRef(false);

  useEffect(() => {
    // Only redirect if logged in and still on login page
    // Do not redirect if we are navigating to role-selection
    if (isLoggedIn && window.location.pathname === '/login' && !isNavigatingToRoleSelection.current) {
      navigate('/home');
    }
  }, [isLoggedIn, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpBackspace = (index, value) => {
    if (!value && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const sendOtp = async () => {
    setIsSendingOtp(true);
    try {
      await axios.post(api.sendOtp, { email: formData.email.trim() });
      setIsOtpSent(true);
      toast.success('OTP sent! Check your email for the OTP.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to send OTP.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim email once
    const email = formData.email.trim();

    // Validate email is present
    if (!email) {
      toast.error('Please enter your email.');
      return;
    }

    const otpValue = otp.join('');
    if (otpValue.length < 4) {
      toast.error('Please enter the 4-digit OTP sent to your email.');
      return;
    }

    try {
      const response = await axios.post(api.login, { email, otp: otpValue });
      const { token, refreshToken, redirectToRoleSelection, roles } = response.data;

      login(token, refreshToken);
      localStorage.setItem('authToken', token);

      if (redirectToRoleSelection) {
        isNavigatingToRoleSelection.current = true;
        toast.info('Multiple roles found. Please select one.');
        setTimeout(() => {
          navigate('/role-selection', { state: { roles } });
        }, 100);
        return;
      }

      toast.success('Login successful! Welcome back!');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const handleOtpKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const otpValue = otp.join('');
      if (otpValue.length === 4) {
        handleSubmit(e);
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await axios.post(api.googleLogin, {
        credential: credentialResponse.credential,
      });

      const { token, refreshToken, redirectToRoleSelection, roles, isNewUser } = response.data;

      login(token, refreshToken);
      localStorage.setItem('authToken', token);

      if (redirectToRoleSelection) {
        isNavigatingToRoleSelection.current = true;
        toast.info('Multiple roles found. Please select one.');
        // Use setTimeout to ensure navigation happens after state updates
        setTimeout(() => {
          navigate('/role-selection', { state: { roles } });
        }, 100);
        return;
      }

      toast.success(isNewUser ? 'Welcome to UlsaApp!' : 'Google login successful! Welcome back!');
      navigate(isNewUser ? '/edit-profile' : '/home');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Google login failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  return (
    <div className="login-bg">
      <div className="login-auth-container">
        <div className="login-welcome-message">
          <img src={logo} alt="Logo" className="login-logo-image" />
          <h2>Welcome Back to UlsaApp!</h2>
          <p>
            &quot;Every login is a step toward growth. Continue your journey of knowledge and success.&quot;
          </p>
          <p className="loginmsg">New to UlsaApp..!👇</p>
          <button
            className="login-rg-button"
            onClick={() => navigate('/signup')}
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-auth-form">
          <h2 className="login-form-title">Login to Continue</h2>

          <div className="login-form-content">
            {/* Google Sign-In Button */}
            <div className="google-login-container">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                text="continue_with"
                shape="rectangular"
                size="large"
                width="100%"
                theme="outline"
              />
            </div>

            {/* Divider */}
            <div className="login-divider">
              <span>OR</span>
            </div>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onKeyPress={(e) => handleKeyPress(e, () => {
                if (!isOtpSent) {
                  sendOtp();
                }
              })}
              placeholder="Email"
              className="login-auth-input"
              required
            />

            {isOtpSent ? (
              <>
                <div className="otp-input-container">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      className="login-otp-box"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace') {
                          handleOtpBackspace(index, e.target.value);
                        } else if (e.key === 'Enter') {
                          handleOtpKeyPress(e);
                        }
                      }}
                      maxLength="1"
                      ref={(el) => (inputRefs.current[index] = el)}
                      inputMode="numeric"
                    />
                  ))}
                </div>
                <button type="submit" className="login-auth-button">Verify OTP</button>
              </>
            ) : (
              <button
                type="button"
                className="login-auth-button"
                onClick={sendOtp}
                disabled={isSendingOtp}
              >
                {isSendingOtp ? 'Sending OTP...' : 'Send OTP'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
